import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const V8_SOURCE = 'https://v8.dev/';
const MOWERY_JS_IMPL = 'https://cseweb.ucsd.edu/~kmowery/papers/js-fingerprinting.pdf';

/**
 * Engine-stack identification via error messages and engine-only features.
 *
 * Two independent axes, both cheap and permission-free:
 *
 *  - LAYOUT engine (Blink / Gecko / WebKit / Goanna). Disambiguated by
 *    error-message wording and by a few layout-specific behaviours.
 *  - SCRIPT engine (V8 / SpiderMonkey / JavaScriptCore). Disambiguated by
 *    V8-only stack-trace formatting and error-message quirks.
 *
 * Why this is a *detection* signal and not just fingerprinting: engine and
 * claimed-brand disagree often under spoofing. A UA claiming Firefox while the
 * error strings are unmistakably V8 means either a broken shim or a
 * deliberately mismatched stack.
 *
 * Credit: the error-message fingerprinting idea goes back to Mowery et al.,
 * "Fingerprinting Information in JavaScript Implementations" (2011), and
 * CreepJS uses the same trick. Implemented here independently.
 */

export const engineStackProbe: Probe = {
  id: 'engineStack',
  label: 'Engine stack',
  layer: 'engine',
  tier: 'client',
  category: 'browser',
  description: 'Layout + script engine identification from error strings and features',
  sources: [MOWERY_JS_IMPL, V8_SOURCE],
  budgetMs: 1500,

  async run(): Promise<ProbeResult> {
    const findings: Finding[] = [];

    // --- collect error messages from guaranteed-typed violations -----------
    const probes: Record<string, string> = {};
    probes.illegalCall = errOf(() => {
      // Calling a class constructor without `new` is a TypeError in every
      // engine. Use a real class so the message is engine-native.
      class Probe {
        constructor() {
          /* never constructed */
        }
      }
      return (Probe as unknown as () => void)();
    });
    probes.nullProto = errOf(() => {
      // Reading a property of a null-prototype object.
      const o = Object.create(null) as Record<string, unknown>;
      return o.missing;
    });
    probes.undefinedCall = errOf(() => {
      const none = undefined as unknown as () => void;
      return none();
    });
    probes.detached = errOf(() => {
      // Array method on a detached receiver.
      const arr = [1, 2, 3] as unknown as { join: () => string };
      const f = arr.join;
      return f.call([] as never);
    });

    // --- stack format tells the script engine -------------------------------
    const err = new Error('engine-probe');
    const stack = err.stack ?? '';
    const stackLines = stack.split('\n');
    const firstStackLine = stackLines[1] ?? '';
    const stackHead = stackLines.slice(0, 3).join(' | ');

    const v8Markers = {
      // V8 prints "at func (file:line:col)" and bare "at file:line:col".
      v8StackShape: /^\s*at\s/.test(stack) && !/^Error/.test(stack.split('\n')[1] ?? 'x'),
      v8ErrorPrefix: /is not a function|is not a constructor|Cannot read propert/.test(
        probes.undefinedCall + probes.nullProto,
      ),
    };

    // Layout engine from UA-independent feature probes.
    const layoutFeatures = {
      // The document.all IsHTMLDDA quirk (falsy-but-truthy) exists on Blink
      // and WebKit, and on nothing else.
      documentAllIsFalsy: (() => {
        const d = document.all as unknown as { length?: number };
        return !!d && !d;
      })(),
      // window.chrome is Blink-only, but it is ABSENT in headless Chromium,
      // so it is a supporting signal rather than a primary one.
      hasChromeObject: typeof (globalThis as { chrome?: unknown }).chrome === 'object',
      // Gecko: InstallTrigger and the moz-prefixed navigator.oscu.
      hasMozInstallTrigger: 'InstallTrigger' in globalThis,
      hasMozOs: 'oscpu' in navigator,
      // WebKit-specific: webkitStorageInfo only. We deliberately do NOT use
      // -webkit-appearance, which Chromium also supports and so cannot
      // discriminate the two.
      hasWebkitStorageInfo:
        typeof (globalThis as { webkitStorageInfo?: unknown }).webkitStorageInfo === 'function',
      // Blink-only: CSS.registerProperty (the @property at-rule). Implemented in
      // Chromium, absent in Gecko and WebKit, and — unlike window.chrome — it is
      // still present in headless Chromium, so it is our primary Blink marker.
      hasCssRegisterProperty: typeof (globalThis as { CSS?: { registerProperty?: unknown } }).CSS
        ?.registerProperty === 'function',
    };

    // Identify.
    const scriptEngine = identifyScriptEngine(v8Markers, firstStackLine);
    const layoutEngine = identifyLayoutEngine(layoutFeatures);

    // --- coherence: UA claims a browser, engine says otherwise ------------
    const ua = navigator.userAgent;
    const claimed = /Firefox\//.test(ua)
      ? 'Gecko'
      : /Edg\/|Chrome\/|Chromium\//.test(ua)
        ? 'Blink'
        : /Safari\//.test(ua) && !/Chrome/.test(ua)
          ? 'WebKit'
          : 'unknown';
    const scriptClaimed = /Firefox\//.test(ua)
      ? 'SpiderMonkey'
      : /Edg\/|Chrome\/|Chromium\//.test(ua)
        ? 'V8'
        : /Safari\//.test(ua) && !/Chrome/.test(ua)
          ? 'JavaScriptCore'
          : 'unknown';

    if (claimed !== 'unknown' && layoutEngine !== 'unknown' && layoutEngine !== claimed) {
      findings.push({
        id: 'enginestack.layout-mismatch',
        severity: 'medium',
        category: 'browser',
        layer: 'engine',
        confidence: 'medium',
        title: `User-Agent claims ${claimed} but the engine behaves like ${layoutEngine}`,
        detail:
          'The layout engine was identified from engine-specific features, and it disagrees with the browser brand in the User-Agent. A genuine browser never has this mismatch; it points to a spoofed UA or a mismatched engine stack.',
        sources: [MOWERY_JS_IMPL],
        evidence: [`UA brand=${claimed}`, `layout engine=${layoutEngine}`],
        scoreContribution: 20,
      });
    }
    if (scriptClaimed !== 'unknown' && scriptEngine !== 'unknown' && scriptEngine !== scriptClaimed) {
      findings.push({
        id: 'enginestack.script-mismatch',
        severity: 'medium',
        category: 'browser',
        layer: 'engine',
        confidence: 'low',
        title: `User-Agent implies ${scriptClaimed} but error strings look like ${scriptEngine}`,
        detail:
          'The script engine was identified from error-message and stack-trace formatting, and it disagrees with the brand implied by the User-Agent.',
        sources: [MOWERY_JS_IMPL],
        evidence: [`UA implies=${scriptClaimed}`, `script engine=${scriptEngine}`],
        scoreContribution: 10,
      });
    }

    return {
      signals: {
        available: ok(true, 'client'),
        // Engine identification
        scriptEngine: ok(scriptEngine, 'derived'),
        layoutEngine: ok(layoutEngine, 'derived'),
        claimedLayoutEngine: ok(claimed, 'derived'),
        claimedScriptEngine: ok(scriptClaimed, 'derived'),
        // Raw error strings (the actual fingerprint material)
        errorIllegalCall: ok(probes.illegalCall, 'client'),
        errorNullProto: ok(probes.nullProto, 'client'),
        errorUndefinedCall: ok(probes.undefinedCall, 'client'),
        errorDetached: ok(probes.detached, 'client'),
        stackHead: ok(stackHead, 'client'),
        firstStackLine: ok(firstStackLine, 'client'),
        v8StackShape: ok(v8Markers.v8StackShape, 'derived'),
        v8ErrorPrefix: ok(v8Markers.v8ErrorPrefix, 'derived'),
        // Engine-only features
        layoutFeatures: ok(layoutFeatures, 'client'),
      },
      findings,
      parts: {
        engineTiming: {
          scriptEngine,
          layoutEngine,
        },
        browserFeatures: {
          hasChromeObject: layoutFeatures.hasChromeObject,
          hasMozInstallTrigger: layoutFeatures.hasMozInstallTrigger,
          hasWebkitStorageInfo: layoutFeatures.hasWebkitStorageInfo,
          hasCssRegisterProperty: layoutFeatures.hasCssRegisterProperty,
        },
      },
    };
  },
};

function errOf(fn: () => unknown): string {
  try {
    fn();
    return 'no-error';
  } catch (e) {
    const err = e as { name?: string; message?: string };
    return `${err?.name ?? 'Error'}: ${err?.message ?? String(e)}`;
  }
}

function identifyScriptEngine(
  v8: { v8StackShape: boolean; v8ErrorPrefix: boolean },
  firstStackLine: string,
): string {
  // V8 formats stack lines as "    at fn (file:line:col)" (or "    at file:line:col").
  // SpiderMonkey uses "fn@file:line:col" and JavaScriptCore "fn@file:line:col".
  // Test V8 FIRST, on the FIRST line only: a later frame can contain an "@"
  // even under V8 (eval'd frames read "at eval (eval at ... <anonymous>)"), so
  // matching "@" anywhere in a multi-line join misclassifies V8 as Gecko.
  if (/^\s*at\s/.test(firstStackLine)) return 'V8';

  // No leading "at": Gecko/JSC style, which uses "fn@file:line:col".
  if (/@/.test(firstStackLine)) {
    return /Safari/.test(navigator.userAgent) ? 'JavaScriptCore' : 'SpiderMonkey';
  }

  // Fall back to the error-message shape.
  if (v8.v8ErrorPrefix) return 'V8';
  return 'unknown';
}

function identifyLayoutEngine(f: Record<string, boolean>): string {
  // Gecko is the most specific, so check it first.
  if (f.hasMozInstallTrigger || f.hasMozOs) return 'Gecko';
  // WebKit: webkitStorageInfo is genuinely WebKit-only. (We do not use
  // -webkit-appearance here — Chromium supports it too.)
  if (f.hasWebkitStorageInfo) return 'WebKit';
  // Blink: CSS.registerProperty is Blink-only and survives headless; window.chrome
  // and the document.all quirk are supporting signals (chrome is absent in
  // headless, and document.all is shared with WebKit).
  if (f.hasCssRegisterProperty || f.hasChromeObject || f.documentAllIsFalsy) return 'Blink';
  return 'unknown';
}

registerProbe(engineStackProbe);
